/* eslint-disable no-undef */
import { valueMappings } from "./dataverse-field-mappings.js";
import { lookupMappings } from "./dataverse-lookup-mappings.js";

export default class FormCaptureBuilder {
    constructor(form, workingFormInfo) {
        this.form = form;
        this.workingFormInfo = workingFormInfo;
        this.formPayload = [];
    }

    generateMappings() {
        const formData = new FormData(this.form);
        const elementsInfo = this.workingFormInfo.ElementsInfo;

        formData.forEach((value, key) => {
            if (!value) return; // Skip if the value is empty

            let inputField = this.form.querySelector(`[name="${key}"]`);
            const inputType = this.getInputType(inputField);
            const friendlyName = this.getFriendlyName(inputField, elementsInfo, key);

            if (inputType === "checkbox" || inputType === "radio") {
                inputField = this.form.querySelector(`[name="${key}"][value="${value}"]`);
            }

            if (!friendlyName || friendlyName === "turnstileRecaptcha") return; // Exclude recaptcha field from D365 payload

            const mappingEntry = {
                DataverseFieldName: friendlyName
            };

            this.handleInputTypeAndValue(inputType, inputField, mappingEntry, value);
        });

        return this.formPayload;
    }

    handleInputTypeAndValue(inputType, inputField, mappingEntry, value) {
        const fieldName = mappingEntry.DataverseFieldName;
        const isMultiple = inputField.hasAttribute("multiple");
        const multiSelectFields = [
            "ht_application",
            "ht_materialshape",
            "ht_materialscut"
        ];

        if ((inputType === "select" && isMultiple) || multiSelectFields.includes(mappingEntry.DataverseFieldName)) {
            const selectedOptions = inputField.hasAttribute("multiple")
                ? Array.from(inputField.selectedOptions).map(option => option.value)
                : [inputField.value];

            mappingEntry.DataverseFieldValue = this.getDataverseFieldValue(mappingEntry.DataverseFieldName, selectedOptions.join(","), true);

            this.addToFormPayload(mappingEntry);

        } else if (inputType === "checkbox" && ["HyperthermAssociatesTopics", "HyperthermTopics", "OMAXTopics", "SoftwareGroupTopics"].includes(fieldName)) {

            const mappingEntry = {};
            mappingEntry.FormFieldName = inputField.id;
            mappingEntry.DataverseFieldName = "msdynmkt_topicid;channels;optinwhenchecked";
            mappingEntry.DataverseFieldValue = value;

            this.formPayload.push(mappingEntry);
        } else {
            switch (inputType) {
                case "radio":
                    if (inputField.checked) {
                        mappingEntry.DataverseFieldValue = this.getDataverseFieldValue(mappingEntry.DataverseFieldName, value);
                        this.addToFormPayload(mappingEntry);
                    }
                    break;
                case "checkbox":
                    mappingEntry.DataverseFieldValue = inputField.checked ? this.getDataverseFieldValue(mappingEntry.DataverseFieldName, value) : null;
                    this.addToFormPayload(mappingEntry);
                    break;
                case "hidden":
                case "text":
                case "email":
                case "textarea":
                    mappingEntry.DataverseFieldValue = this.getDataverseFieldValue(mappingEntry.DataverseFieldName, value);
                    this.addToFormPayload(mappingEntry);
                    break;
                case "select":
                    mappingEntry.DataverseFieldValue = this.getDataverseFieldValue(mappingEntry.DataverseFieldName, inputField.options[inputField.selectedIndex].value);
                    this.addToFormPayload(mappingEntry);
                    break;
                default:
                    //console.warn(`Unhandled input type: ${inputType}`);
                    break;
            }
        }
    }

    addToFormPayload(mappingEntry) {
        const existingEntry = this.formPayload.find(entry => entry.DataverseFieldName === mappingEntry.DataverseFieldName);
        if (!existingEntry) {
            this.formPayload.push(mappingEntry);
        }
    }

    getFriendlyName(inputField, elementsInfo, key) {
        const friendlyName = inputField.getAttribute("data-element-friendly-name");
        if (friendlyName) return friendlyName;

        const elementInfo = key.includes("__TempData") ?
            elementsInfo[key.replace("__TempData", "")] :
            elementsInfo[key];

        return elementInfo && elementInfo.friendlyName ? elementInfo.friendlyName : "";
    }

    getInputType(inputField) {
        if (!inputField) return null;

        const tagName = inputField.tagName.toLowerCase();
        switch (tagName) {
            case "input":
                return inputField.type.toLowerCase();
            case "textarea":
                return "textarea";
            case "select":
                return "select";
            case "button":
                return "button";
            default:
                return "unknown";
        }
    }

    getDataverseFieldValue(formFieldName, formValue, isMultiSelect = false) {
        const lookupFields = {
            ht_countryisolookup: "ht_countrycodes",
            ht_address1subdivisioniso: "ht_isosubdivisioncodes",
            ht_calltoaction: "ht_calltoaction",
            ht_preferredcommunicationlanguage: "ht_preferredcommunicationlanguage",
            "ht_calltoaction": "ht_calltoaction"
        };

        if (lookupFields[formFieldName]) {
            const logicalName = lookupFields[formFieldName];
            let fieldMapping;

            if (formFieldName === "ht_address1subdivisioniso") {
                const countryField = this.form.querySelector("div.ht_countryisolookup select");
                const countryValue = countryField ? countryField.value : null;

                const stateMappings = {
                    "US": lookupMappings.us,
                    "CA": lookupMappings.ca,
                    "AU": lookupMappings.au,
                    "NZ": lookupMappings.nz,
                    "CN": lookupMappings.cn
                };

                fieldMapping = stateMappings[countryValue] ? stateMappings[countryValue][formValue] : null;
            } else {
                fieldMapping = lookupMappings[formFieldName][formValue] ? lookupMappings[formFieldName][formValue] || formValue : formValue;
            }

            if (fieldMapping) {
                return `{"Id": "${fieldMapping}", "LogicalName": "${logicalName}"}`;
            }
        }

        if (isMultiSelect) {
            const valuesArray = formValue.split(",").map(value => valueMappings[formFieldName] ? valueMappings[formFieldName][value] || value : value);
            return JSON.stringify(valuesArray);
        }

        return valueMappings[formFieldName] ? valueMappings[formFieldName][formValue] || formValue : formValue;
    }
}
